import { Component } from "@theme/component";
import { onAnimationEnd } from "@theme/utilities";
import { StandardEvents } from "@shopify/events";
import { trackCartRequest, applyCurrentCartCount } from "@theme/section-renderer";
class CartIcon extends Component {
  constructor() {
    super(...arguments);
    this.requiredRefs = ["cartBubble", "cartBubbleText", "cartBubbleCount"];
    this.cartReadControllers = /* @__PURE__ */ new Set();
    this.cartLifecycle = 0;
    this.cartDetached = false;
    this.bubbleGeneration = 0;
    this.pendingBubbleFrame = null;
    this.onPageShow = (event) => {
      if (event.persisted) this.ensureCartBubbleIsCorrect();
    };
    this.onCartUpdate = async (event) => {
      if (!event.promise) return;
      const ticket = trackCartRequest(event.promise);
      const epoch = this.cartLifecycle;
      const controller = new AbortController();
      this.cartReadControllers.add(controller);
      try {
        const { cart, detail } = await event.promise;
        if (this.cartDetached || this.cartLifecycle !== epoch) return;
        await applyCurrentCartCount(
          cart && cart.totalQuantity,
          detail && detail.source === "pb-card-add" ? null : ticket,
          controller,
          (count) => this.renderCartBubble(count)
        );
      } catch (error) {
        if (error && error.name !== "AbortError" && !this.cartDetached && this.cartLifecycle === epoch) console.warn("[cart-icon] Count refresh failed:", error);
      } finally {
        this.cartReadControllers.delete(controller);
      }
    };
  }
  cancelBubbleFrame() {
    if (this.pendingBubbleFrame) {
      cancelAnimationFrame(this.pendingBubbleFrame.id);
      this.pendingBubbleFrame.resolve();
      this.pendingBubbleFrame = null;
    }
  }
  async renderCartBubble(itemCount, animate = true) {
    if (this.cartDetached || !Number.isInteger(itemCount) || itemCount < 0) return;
    const generation = ++this.bubbleGeneration;
    this.cancelBubbleFrame();
    this.refs.cartBubble.classList.remove("cart-bubble--animating");
    this.refs.cartBubbleCount.classList.toggle("hidden", itemCount === 0);
    this.refs.cartBubble.classList.toggle("visually-hidden", itemCount === 0);
    this.currentCartCount = itemCount;
    for (const badge of document.querySelectorAll("[data-cart-count]")) {
      badge.textContent = String(itemCount);
      badge.hidden = itemCount === 0;
    }
    this.classList.toggle("header-actions__cart-icon--has-cart", itemCount > 0);
    this.dispatchEvent(new CustomEvent("pb:cart-count-confirmed", { bubbles: true, detail: { count: itemCount } }));
    if (!animate || itemCount === 0) return;
    await new Promise((resolve) => {
      this.pendingBubbleFrame = { resolve, id: requestAnimationFrame(() => {
        this.pendingBubbleFrame = null;
        resolve();
      }) };
    });
    if (this.cartDetached || generation !== this.bubbleGeneration) return;
    this.refs.cartBubble.classList.add("cart-bubble--animating");
    try {
      await onAnimationEnd(this.refs.cartBubbleText);
    } finally {
      if (generation === this.bubbleGeneration) this.refs.cartBubble.classList.remove("cart-bubble--animating");
    }
  }
  async ensureCartBubbleIsCorrect() {
    const epoch = this.cartLifecycle;
    const controller = new AbortController();
    this.cartReadControllers.add(controller);
    try {
      await applyCurrentCartCount(null, null, controller, (count) => {
        if (!this.cartDetached && this.cartLifecycle === epoch) return this.renderCartBubble(count, false);
      });
    } catch (error) {
      if (error && error.name !== "AbortError" && !this.cartDetached && this.cartLifecycle === epoch) console.warn("[cart-icon] Count refresh failed:", error);
    } finally {
      this.cartReadControllers.delete(controller);
    }
  }
  get currentCartCount() {
    return this.cartItemCount === void 0 ? parseInt(this.refs.cartBubbleCount.textContent, 10) || 0 : this.cartItemCount;
  }
  set currentCartCount(value) {
    this.cartItemCount = value;
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : "";
  }
  connectedCallback() {
    super.connectedCallback();
    this.cartDetached = false;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.addEventListener("pb:cart-count-refresh", this.onCartUpdate);
    window.addEventListener("pageshow", this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.cartDetached = true;
    this.cartLifecycle++;
    this.bubbleGeneration++;
    this.cancelBubbleFrame();
    this.refs.cartBubble.classList.remove("cart-bubble--animating");
    for (const controller of this.cartReadControllers) controller.abort();
    this.cartReadControllers.clear();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.removeEventListener("pb:cart-count-refresh", this.onCartUpdate);
    window.removeEventListener("pageshow", this.onPageShow);
  }
}
if (!customElements.get("cart-icon")) customElements.define("cart-icon", CartIcon);
